import {useCallback, useEffect, useState} from "react";
import {isScrollGateSatisfied} from "../utility/consent";

export interface UseScrollGateOptions {
	/**
	 * Only measure while true. Keeps an empty or still-loading container from satisfying the gate.
	 */
	enabled?: boolean;
}

export interface UseScrollGateReturn {
	/**
	 * Attach to the scroll container being gated.
	 */
	ref: (element: HTMLDivElement | null) => void;
	/**
	 * True once the respondent has reached the end of the container. Stays true until `reset`.
	 */
	reached: boolean;
	/**
	 * Re-measure the container, e.g. after content finished rendering.
	 */
	recheck: () => void;
	/**
	 * Forget that the end was reached, e.g. when the content is replaced.
	 */
	reset: () => void;
}

/**
 * Tracks whether a scroll container has been scrolled to its end. Content that does not
 * overflow satisfies the gate as soon as it is measured.
 */
export const useScrollGate = ({enabled = true}: UseScrollGateOptions = {}): UseScrollGateReturn => {
	const [element, setElement] = useState<HTMLDivElement | null>(null);
	const [reached, setReached] = useState(false);

	const recheck = useCallback((): void => {
		if (!element || !enabled) return;
		if (isScrollGateSatisfied(element)) setReached(true);
	}, [element, enabled]);

	const reset = useCallback((): void => setReached(false), []);

	useEffect(() => {
		if (!element || !enabled) return undefined;
		element.addEventListener("scroll", recheck, {passive: true});
		const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(recheck);
		observer?.observe(element);
		// Measure once layout has settled; content that fits without scrolling passes right away.
		const frame = requestAnimationFrame(recheck);
		return () => {
			cancelAnimationFrame(frame);
			element.removeEventListener("scroll", recheck);
			observer?.disconnect();
		};
	}, [element, enabled, recheck]);

	return {ref: setElement, reached, recheck, reset};
};
